import {MerchantRatesSection} from "./merchant-details/MerchantRatesSection";
import {MerchantSellAddressesSection} from "./merchant-details/MerchantSellAddressesSection";
import React, {useEffect, useState} from "react";
import "./merchant-details/layout.css";
import {useAuth} from "../../auth";
import {BalanceMovementsTable, ErrorBanner, Modal, notificationTypeLabel, SecondFactorControl} from "../../components";
import {
    formatDate,
    generateMerchantPortalPassword,
    normalizeMerchantPortalLogin,
    validateMerchantPortalCredentials,
} from "../../utils";
import {
    applyMerchantBalanceOperation,
    getDebtDiscrepancies,
    archiveMerchant,
    backfillMerchantChainDeposits,
    deleteMerchantPortalAccess,
    disableMerchantDepositAddress,
    enableMerchantDepositAddress,
    formatApiError,
    formatChainBackfillError,
    formatDepositAddressError,
    getMerchantChainDeposits,
    merchantChainDepositExplorerUrl,
    getMerchantDepositAddresses,
    getMerchant,
    getMerchantBalanceAvailability,
    getMerchantBalanceByTeam,
    getMerchantBalanceMovements,
    isMirroredMerchant,
    listNotifications,
    markNotificationRead,
    getMerchantPortalAccess,
    putMerchantPortalAccess,
    resetMerchantBalance,
    revokeMerchantApiToken,
    rotateMerchantApiToken,
    setMerchantDepositAddress,
    updateMerchant,
} from "../../api";
import type {
    BalanceByTeamResponse,
    BalanceMovementItem,
    MerchantAPITokenResponse,
    MerchantBalanceAvailabilityResponse,
    DebtDiscrepancyItem,
    MerchantBalanceOperationType,
    MerchantChainBackfillResponse,
    MerchantChainDeposit,
    MerchantDepositAddress,
    MerchantPortalAccessResponse,
    MerchantResponse,
    NotificationItem,
    UpdateMerchantRequest,
} from "../../api";
import {
    CabinetBookSection,
    ExternalMerchantSection,
    PlatformClosedOrdersSection,
    HistoryPagination,
    LIST_ONLY_HINT,
    NOTHING_TO_SETTLE_NOTICE,
    describeDiscrepancy,
    formatMerchantBalance,
    formatSettlementMode,
    formatSettlementSetting,
    isPositiveAmountString,
    manualOperationFormState,
    manualOperationIsListOnly,
    newOperationKey,
    orderShapeComesFromCabinet,
    shortChainValue,
    validateUsdtAmount,
    withdrawalButtonState,
    withdrawalExceedsFreeFunds,
} from "./merchant-details";

// Границы берём из бэкенда (internal/merchant/handler.go).
const MAX_PAYMENTS_MIN = 1;
const MAX_PAYMENTS_MAX = 10;
const CLAIM_HOLD_MIN = 1;
const TRON_ADDRESS_PATTERN = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const HISTORY_PAGE_SIZE = 10;
const HISTORY_FETCH_LIMIT = 200;

interface MerchantDetailsProps {
    merchantId: string;
    onBack: () => void;
    onArchived?: (merchantId: string) => void;
}

/**
 * Детали мерчанта: GET /v1/merchant/{id} + правка двух настроек (PATCH) +
 * ротация/отзыв API-токена. Состояние: свежие данные с бэкенда, три модалки
 * (правка / показ нового токена / подтверждение отзыва). Сырой токен живёт
 * только в стейте, пока открыта модалка rotate, и НЕ логируется.
 */
export function MerchantDetails({
    merchantId,
    onBack,
    onArchived,
}: MerchantDetailsProps): JSX.Element {
    const {token} = useAuth();

    const [merchant, setMerchant] = useState<MerchantResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);
    const [success, setSuccess] = useState("");

    // Edit modal
    const [editOpen, setEditOpen] = useState(false);
    const [editMaxPayments, setEditMaxPayments] = useState("");
    const [editClaimHold, setEditClaimHold] = useState("");
    const [editIsVip, setEditIsVip] = useState(false);
    const [editError, setEditError] = useState<unknown>(null);
    const [editSaving, setEditSaving] = useState(false);

    // Settlement mode modal (овердрафт / предоплата + оба поля видны всегда).
    const [modeOpen, setModeOpen] = useState(false);
    const [modeEnabled, setModeEnabled] = useState(true);
    const [modeLimit, setModeLimit] = useState("");
    const [modeError, setModeError] = useState<unknown>(null);
    const [modeSaving, setModeSaving] = useState(false);

    // Модалка «Курс и комиссия»: источник курса + ставка. Оба поля бэк принимает
    // только парой, поэтому и здесь они живут одной формой.


    // Меню «Ещё действия» (⋯) в шапке: popover с опасными действиями.
    const [actionsMenuOpen, setActionsMenuOpen] = useState(false);
    const actionsMenuRef = React.useRef<HTMLDivElement>(null);

    // Уведомления мерчанта (active + resolved).
    const [notifications, setNotifications] = useState<NotificationItem[]>([]);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const [notificationsError, setNotificationsError] = useState<unknown>(null);

    // Token rotate modal (show-once)
    const [tokenResp, setTokenResp] = useState<MerchantAPITokenResponse | null>(
        null,
    );
    const [tokenCopied, setTokenCopied] = useState(false);
    const [rotateError, setRotateError] = useState<unknown>(null);
    const [rotating, setRotating] = useState(false);

    // Revoke confirm modal
    const [revokeOpen, setRevokeOpen] = useState(false);
    const [revokeError, setRevokeError] = useState<unknown>(null);
    const [revoking, setRevoking] = useState(false);

    // Archive confirm modal
    const [archiveOpen, setArchiveOpen] = useState(false);
    const [archiveError, setArchiveError] = useState<unknown>(null);
    const [archiving, setArchiving] = useState(false);

    // Merchant browser-portal access. Passwords exist only while the credential
    // modal is open and are cleared on every close and successful submit.
    const [portalAccess, setPortalAccess] = useState<MerchantPortalAccessResponse | null>(null);
    const [portalAccessLoading, setPortalAccessLoading] = useState(false);
    const [portalAccessError, setPortalAccessError] = useState<unknown>(null);
    const [portalCredentialOpen, setPortalCredentialOpen] = useState(false);
    const [portalLogin, setPortalLogin] = useState("");
    const [portalPassword, setPortalPassword] = useState("");
    const [portalPasswordConfirmation, setPortalPasswordConfirmation] = useState("");
    const [portalPasswordVisible, setPortalPasswordVisible] = useState(false);
    const [portalCredentialError, setPortalCredentialError] = useState<unknown>(null);
    const [portalCredentialSaving, setPortalCredentialSaving] = useState(false);
    const [portalDisableOpen, setPortalDisableOpen] = useState(false);
    const [portalDisableError, setPortalDisableError] = useState<unknown>(null);
    const [portalDisabling, setPortalDisabling] = useState(false);

    // Balance reset confirm modal
    const [resetOpen, setResetOpen] = useState(false);
    const [resetError, setResetError] = useState<unknown>(null);
    const [resetting, setResetting] = useState(false);

    // ДДС мерчанта: ручные пополнения и выводы.
    const [operationOpen, setOperationOpen] = useState(false);
    const [operationType, setOperationType] = useState<MerchantBalanceOperationType>("topup");
    const [operationAmount, setOperationAmount] = useState("");
    // Ключ идемпотентности живёт с момента открытия формы и до её закрытия: он
    // именует ОПЕРАЦИЮ, а не попытку, поэтому повторный сабмит после сетевой
    // ошибки не может провести деньги дважды.
    const [operationKey, setOperationKey] = useState("");
    // А это ключ, введённый админом. Пусто — работает сгенерированный выше;
    // заполнено — ключ задан процедурой снаружи формы, и придумывать свой нельзя:
    // коррекция долгового кабинета опознаётся сверкой именно по нему.
    // Открытые расхождения сверок долгового кабинета. Ключ идемпотентности
    // раньше набирался руками из текста алерта — с опечаткой в одном символе
    // проводка проходила, деньги двигались, а алерт оставался красным навсегда.
    // Теперь оператор выбирает расхождение строкой, а сумму, направление и ключ
    // подставляет ответ сверки.
    const [discrepancies, setDiscrepancies] = useState<DebtDiscrepancyItem[]>([]);
    const [pickedDiscrepancy, setPickedDiscrepancy] = useState("");
    const [operationError, setOperationError] = useState<unknown>(null);
    const [operationSubmitting, setOperationSubmitting] = useState(false);
    const [availability, setAvailability] = useState<MerchantBalanceAvailabilityResponse | null>(null);
    const [availabilityError, setAvailabilityError] = useState("");
    const [movements, setMovements] = useState<BalanceMovementItem[]>([]);
    const [movementsCurrency, setMovementsCurrency] = useState("");
    const [movementsLoading, setMovementsLoading] = useState(false);
    const [movementsError, setMovementsError] = useState("");
    const [movementsPage, setMovementsPage] = useState(0);
    const [byTeam, setByTeam] = useState<BalanceByTeamResponse | null>(null);
    const [byTeamLoading, setByTeamLoading] = useState(false);
    const [byTeamError, setByTeamError] = useState<unknown>(null);

    // Watch-only TRC20 address and credited deposit history.
    const [depositAddresses, setDepositAddresses] = useState<MerchantDepositAddress[]>([]);
    const [depositAddressesLoading, setDepositAddressesLoading] = useState(false);
    const [depositAddressesError, setDepositAddressesError] = useState("");
    const [chainDeposits, setChainDeposits] = useState<MerchantChainDeposit[]>([]);
    const [chainDepositsLoading, setChainDepositsLoading] = useState(false);
    const [chainDepositsError, setChainDepositsError] = useState("");
    const [chainDepositsPage, setChainDepositsPage] = useState(0);
    const [bindAddressOpen, setBindAddressOpen] = useState(false);
    const [bindAddressValue, setBindAddressValue] = useState("");
    const [bindAddressError, setBindAddressError] = useState("");
    const [bindingAddress, setBindingAddress] = useState(false);
    const [addressToDisable, setAddressToDisable] = useState<MerchantDepositAddress | null>(null);
    const [addressToEnable, setAddressToEnable] = useState<MerchantDepositAddress | null>(null);
    const [addressLifecycleError, setAddressLifecycleError] = useState("");
    const [addressLifecycleSubmitting, setAddressLifecycleSubmitting] = useState(false);
    const [showDisabledAddresses, setShowDisabledAddresses] = useState(false);
    const [copiedAddress, setCopiedAddress] = useState("");
    const [backfillOpen, setBackfillOpen] = useState(false);
    const [backfillHours, setBackfillHours] = useState("24");
    const [backfillError, setBackfillError] = useState("");
    const [backfillResult, setBackfillResult] = useState<MerchantChainBackfillResponse | null>(null);
    const [backfilling, setBackfilling] = useState(false);

    const loadMovements = React.useCallback((): void => {
        if (!token) {
            setMovements([]);
            setMovementsCurrency("");
            setMovementsError("");
            setMovementsPage(0);
            return;
        }
        const authToken = token;
        setMovementsLoading(true);
        setMovementsError("");
        setMovementsPage(0);

        const fetchAllPages = async (): Promise<{items: BalanceMovementItem[]; currency: string}> => {
            const items: BalanceMovementItem[] = [];
            let currency = "";
            let offset = 0;

            for (;;) {
                const resp = await getMerchantBalanceMovements(authToken, merchantId, {
                    limit: HISTORY_FETCH_LIMIT,
                    offset,
                });
                items.push(...resp.items);
                currency = resp.currency;

                if (resp.nextOffset == null) {
                    return {items, currency};
                }
                offset = resp.nextOffset;
            }
        };

        fetchAllPages()
            .then((resp) => {
                setMovements(resp.items);
                setMovementsCurrency(resp.currency);
            })
            .catch((err) => {
                setMovements([]);
                setMovementsCurrency("");
                setMovementsError(formatApiError(err));
            })
            .finally(() => setMovementsLoading(false));
    }, [merchantId, token]);

    const loadNotifications = React.useCallback((): void => {
        if (!token) {
            setNotifications([]);
            setNotificationsError(null);
            return;
        }
        setNotificationsLoading(true);
        setNotificationsError(null);
        listNotifications(token, {merchantId, limit: 50})
            .then((resp) => setNotifications(resp.items))
            .catch((err) => {
                setNotifications([]);
                setNotificationsError(err);
            })
            .finally(() => setNotificationsLoading(false));
    }, [merchantId, token]);

    const markNotificationAsRead = (id: string): void => {
        if (!token) return;
        markNotificationRead(token, id)
            .then(loadNotifications)
            .catch((err) => setNotificationsError(err));
    };

    const loadByTeam = React.useCallback((): void => {
        if (!token) {
            setByTeam(null);
            setByTeamError(null);
            return;
        }
        setByTeamLoading(true);
        setByTeamError(null);
        getMerchantBalanceByTeam(token, merchantId)
            .then(setByTeam)
            .catch((err) => {
                setByTeam(null);
                setByTeamError(err);
            })
            .finally(() => setByTeamLoading(false));
    }, [merchantId, token]);

    const loadDepositAddresses = React.useCallback((): void => {
        if (!token) {
            setDepositAddresses([]);
            setDepositAddressesError("");
            return;
        }
        setDepositAddressesLoading(true);
        setDepositAddressesError("");
        getMerchantDepositAddresses(token, merchantId)
            .then((resp) => setDepositAddresses(resp.items))
            .catch((err) => {
                setDepositAddresses([]);
                setDepositAddressesError(formatDepositAddressError(err));
            })
            .finally(() => setDepositAddressesLoading(false));
    }, [merchantId, token]);

    const loadChainDeposits = React.useCallback((): void => {
        if (!token) {
            setChainDeposits([]);
            setChainDepositsError("");
            setChainDepositsPage(0);
            return;
        }
        setChainDepositsLoading(true);
        setChainDepositsError("");
        setChainDepositsPage(0);
        getMerchantChainDeposits(token, merchantId, HISTORY_FETCH_LIMIT)
            .then((resp) => setChainDeposits(resp.items))
            .catch((err) => {
                setChainDeposits([]);
                setChainDepositsError(formatDepositAddressError(err));
            })
            .finally(() => setChainDepositsLoading(false));
    }, [merchantId, token]);

    const loadPortalAccess = React.useCallback((): void => {
        if (!token) {
            setPortalAccess(null);
            setPortalAccessError(null);
            return;
        }
        setPortalAccessLoading(true);
        setPortalAccessError(null);
        getMerchantPortalAccess(token, merchantId)
            .then(setPortalAccess)
            .catch((err) => {
                setPortalAccess(null);
                setPortalAccessError(err);
            })
            .finally(() => setPortalAccessLoading(false));
    }, [merchantId, token]);

    const reload = React.useCallback((): void => {
        if (!token) {
            setMovements([]);
            setMovementsCurrency("");
            setMovementsError("");
            setMovementsPage(0);
            return;
        }
        setLoading(true);
        setError(null);
        getMerchant(token, merchantId)
            .then((m) => {
                setMerchant(m);
                loadMovements();
                loadByTeam();
                loadNotifications();
                loadDepositAddresses();
                loadChainDeposits();
                loadPortalAccess();
            })
            .catch((err) => setError(err))
            .finally(() => setLoading(false));
    }, [
        loadByTeam,
        loadChainDeposits,
        loadDepositAddresses,
        loadMovements,
        loadNotifications,
        loadPortalAccess,
        token,
        merchantId,
    ]);

    useEffect(() => {
        reload();
    }, [reload]);

    useEffect(() => {
        // Route changes unmount/reuse this component without carrying a secret
        // to another merchant. Nothing is persisted in storage or URL.
        setPortalCredentialOpen(false);
        setPortalLogin("");
        setPortalPassword("");
        setPortalPasswordConfirmation("");
        setPortalCredentialError(null);
        setPortalDisableOpen(false);
        setPortalDisableError(null);
    }, [merchantId]);

    // Закрытие меню «⋯» по клику вне и по Escape (паттерн NotificationCenter).
    useEffect(() => {
        if (!actionsMenuOpen) return;
        const onDocClick = (event: MouseEvent): void => {
            if (
                actionsMenuRef.current &&
                !actionsMenuRef.current.contains(event.target as Node)
            ) {
                setActionsMenuOpen(false);
            }
        };
        const onKey = (event: KeyboardEvent): void => {
            if (event.key === "Escape") setActionsMenuOpen(false);
        };
        document.addEventListener("mousedown", onDocClick);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDocClick);
            document.removeEventListener("keydown", onKey);
        };
    }, [actionsMenuOpen]);

    const openEdit = (): void => {
        // Поля пустые по умолчанию: PATCH опциональный, пустое = не менять.
        // Текущее значение видно в placeholder.
        setEditMaxPayments("");
        setEditClaimHold("");
        setEditIsVip(Boolean(merchant?.isVip));
        setEditError(null);
        setEditOpen(true);
    };

    const openMode = (): void => {
        // Поле показываем текущим значением (не placeholder), чтобы сохранять
        // его при переключении режима; "" = лимит/допуск не задан.
        setModeEnabled(merchant?.overdraftEnabled ?? true);
        setModeLimit(merchant?.overdraftLimit ?? "");
        setModeError(null);
        setModeOpen(true);
    };

    const submitEdit = (): void => {
        if (!token) return;
        setEditError(null);

        const body: UpdateMerchantRequest = {};
        const maxPayments = editMaxPayments.trim();
        const claimHold = editClaimHold.trim();

        if (maxPayments !== "") {
            const n = Number(maxPayments);
            if (
                !Number.isInteger(n) ||
                n < MAX_PAYMENTS_MIN ||
                n > MAX_PAYMENTS_MAX
            ) {
                setEditError(
                    `maxPaymentsPerOrder: целое от ${MAX_PAYMENTS_MIN} до ${MAX_PAYMENTS_MAX}.`,
                );
                return;
            }
            body.maxPaymentsPerOrder = n;
        }
        if (claimHold !== "") {
            const n = Number(claimHold);
            if (!Number.isInteger(n) || n < CLAIM_HOLD_MIN) {
                setEditError(
                    `claimHoldMinutes: целое не меньше ${CLAIM_HOLD_MIN}.`,
                );
                return;
            }
            body.claimHoldMinutes = n;
        }
        if (merchant && editIsVip !== merchant.isVip) {
            body.isVip = editIsVip;
        }

        if (
            body.maxPaymentsPerOrder === undefined &&
            body.claimHoldMinutes === undefined &&
            body.isVip === undefined
        ) {
            setEditError("Заполните хотя бы одно поле — пустые поля не меняются.");
            return;
        }

        setEditSaving(true);
        updateMerchant(token, merchantId, body)
            .then((m) => {
                setMerchant(m);
                setEditOpen(false);
                setSuccess("Настройки мерчанта обновлены.");
                loadNotifications();
            })
            .catch((err) => setEditError(err))
            .finally(() => setEditSaving(false));
    };

    const submitMode = (): void => {
        if (!token || !merchant) return;
        setModeError(null);

        const body: UpdateMerchantRequest = {};
        if (modeEnabled !== merchant.overdraftEnabled) {
            body.overdraftEnabled = modeEnabled;
        }
        // Пустая строка = сброс поля в null (COALESCE-паттерн на бэке). Одно
        // поле обслуживает оба режима: лимит долга в овердрафте, допуск — в
        // предоплате.
        const limit = modeLimit.trim();
        if (limit !== (merchant.overdraftLimit ?? "")) {
            if (limit !== "") {
                const problem = validateUsdtAmount(limit);
                if (problem) {
                    setModeError(
                        `${modeEnabled ? "Лимит долга" : "Допуск овердрафта"}: ${problem}.`,
                    );
                    return;
                }
            }
            body.overdraftLimit = limit;
        }

        if (
            body.overdraftEnabled === undefined &&
            body.overdraftLimit === undefined
        ) {
            setModeError("Ничего не изменилось.");
            return;
        }

        setModeSaving(true);
        updateMerchant(token, merchantId, body)
            .then((m) => {
                setMerchant(m);
                setModeOpen(false);
                setSuccess("Режим расчётов обновлён.");
                loadNotifications();
            })
            .catch((err) => setModeError(err))
            .finally(() => setModeSaving(false));
    };

    const clearPortalCredentialSecret = (): void => {
        setPortalPassword("");
        setPortalPasswordConfirmation("");
        setPortalPasswordVisible(false);
    };

    const openPortalCredential = (): void => {
        setPortalLogin(portalAccess?.login ?? "");
        clearPortalCredentialSecret();
        setPortalCredentialError(null);
        setPortalCredentialOpen(true);
    };

    const closePortalCredential = (): void => {
        if (portalCredentialSaving) return;
        setPortalCredentialOpen(false);
        setPortalLogin("");
        clearPortalCredentialSecret();
        setPortalCredentialError(null);
    };

    const submitPortalCredential = (): void => {
        if (!token) return;
        const problem = validateMerchantPortalCredentials(
            portalLogin,
            portalPassword,
            portalPasswordConfirmation,
        );
        if (problem) {
            setPortalCredentialError(problem);
            return;
        }
        setPortalCredentialSaving(true);
        setPortalCredentialError(null);
        putMerchantPortalAccess(token, merchantId, {
            login: normalizeMerchantPortalLogin(portalLogin),
            password: portalPassword,
        })
            .then((access) => {
                setPortalAccess(access);
                setPortalCredentialOpen(false);
                setPortalLogin("");
                clearPortalCredentialSecret();
                setSuccess(
                    portalAccess?.enabled
                        ? "Пароль личного кабинета сброшен. Все прежние сессии завершены."
                        : "Доступ в личный кабинет выдан.",
                );
            })
            .catch((err) => setPortalCredentialError(err))
            .finally(() => setPortalCredentialSaving(false));
    };

    const closePortalDisable = (): void => {
        if (portalDisabling) return;
        setPortalDisableOpen(false);
        setPortalDisableError(null);
    };

    const confirmPortalDisable = (): void => {
        if (!token) return;
        setPortalDisabling(true);
        setPortalDisableError(null);
        deleteMerchantPortalAccess(token, merchantId)
            .then((access) => {
                setPortalAccess(access);
                setPortalDisableOpen(false);
                setSuccess("Доступ в личный кабинет отключён. Все сессии завершены.");
            })
            .catch((err) => setPortalDisableError(err))
            .finally(() => setPortalDisabling(false));
    };

    const rotateToken = (): void => {
        if (!token) return;
        setRotateError(null);
        setRotating(true);
        rotateMerchantApiToken(token, merchantId)
            .then((resp) => {
                // Сырой токен показываем один раз: живёт только в стейте модалки.
                setTokenResp(resp);
                setTokenCopied(false);
                setSuccess("");
            })
            .catch((err) => setRotateError(err))
            .finally(() => setRotating(false));
    };

    const closeTokenModal = (): void => {
        setTokenResp(null);
        setTokenCopied(false);
        setSuccess("Новый токен показан. Сохраните его — повторно он недоступен.");
    };

    const copyToken = async (): Promise<void> => {
        const raw = tokenResp?.token;
        if (!raw) return;
        try {
            await navigator.clipboard.writeText(raw);
            setTokenCopied(true);
        } catch {
            setTokenCopied(false);
        }
    };

    const confirmRevoke = (): void => {
        if (!token) return;
        setRevokeError(null);
        setRevoking(true);
        revokeMerchantApiToken(token, merchantId)
            .then(() => {
                setRevokeOpen(false);
                setSuccess("Активный API-токен отозван.");
            })
            .catch((err) => setRevokeError(err))
            .finally(() => setRevoking(false));
    };

    const confirmArchive = (): void => {
        if (!token) return;
        setArchiveError(null);
        setArchiving(true);
        archiveMerchant(token, merchantId)
            .then(() => {
                setArchiveOpen(false);
                if (onArchived) {
                    onArchived(merchantId);
                } else {
                    onBack();
                }
            })
            .catch((err) => setArchiveError(err))
            .finally(() => setArchiving(false));
    };

    const confirmResetBalance = (): void => {
        if (!token || !merchant) return;
        setResetError(null);
        setResetting(true);
        resetMerchantBalance(token, merchantId)
            .then((m) => {
                setMerchant(m);
                loadMovements();
                loadByTeam();
                setResetOpen(false);
                setSuccess(`Баланс обнулён: ${formatMerchantBalance(m)}.`);
            })
            .catch((err) => setResetError(err))
            .finally(() => setResetting(false));
    };

    const openOperation = (type: MerchantBalanceOperationType): void => {
        setActionsMenuOpen(false);
        setOperationType(type);
        setOperationAmount("");
        setOperationError(null);
        // Ключ формы — это и есть «просто движение денег»: имя, которого сверка не
        // знает. У долгового кабинета его не генерируем вовсе, чтобы он не мог
        // уехать с запросом ни по какой ветке кода.
        setOperationKey(manualOperationIsListOnly(merchant) ? "" : newOperationKey());
        setDiscrepancies([]);
        setPickedDiscrepancy("");
        setAvailability(null);
        setAvailabilityError("");
        setOperationOpen(true);
        if (!token) return;
        // Расхождения есть только у долгового кабинета; у обычного мерчанта
        // ручка отказывает, и это не ошибка формы — просто списка нет.
        getDebtDiscrepancies(token, merchantId)
            .then((resp) => setDiscrepancies(resp.items.filter((item) => item.operationKey)))
            .catch(() => setDiscrepancies([]));
        // Подсказка «в работе ~X USDT»: без неё админ выведет деньги, не зная,
        // что часть уже обещана взятым заявкам (вывод их не блокирует — решение 8).
        getMerchantBalanceAvailability(token, merchantId)
            .then(setAvailability)
            .catch((err) => setAvailabilityError(formatApiError(err)));
    };

    const submitOperation = (): void => {
        if (!token || !merchant) return;
        const form = manualOperationFormState({
            merchant,
            discrepancies,
            pickedKey: pickedDiscrepancy,
            amount: operationAmount,
            generatedKey: operationKey,
            submitting: operationSubmitting,
        });
        // Долговой кабинет без выбранной строки: имени у операции нет, отправлять
        // нечего. Кнопка в этот момент уже выключена — здесь страховка от ветки,
        // которая её обойдёт.
        if (form.operationKey === null) return;
        const amount = operationAmount.trim();
        const problem = validateUsdtAmount(amount);
        if (problem !== null) {
            setOperationError(new Error(`Сумма: ${problem}.`));
            return;
        }
        if (!isPositiveAmountString(amount)) {
            setOperationError(new Error("Сумма: должна быть больше нуля."));
            return;
        }
        setOperationError(null);
        setOperationSubmitting(true);
        applyMerchantBalanceOperation(token, merchantId, {
            type: operationType,
            amount,
            // Имя выбранного расхождения — то, по чему сверка узнает проводку и
            // погасит алерт. У обычного мерчанта без выбора идёт имя формы: оно
            // защищает от двойного клика и больше ни на что не влияет.
            operationKey: form.operationKey,
        })
            .then((resp) => {
                setMerchant((prev) => (prev ? {...prev, amount: resp.balance} : prev));
                setSuccess(
                    `${operationType === "topup" ? "Пополнение" : "Вывод"} ${resp.operation.amount} ${resp.currency} проведено. Баланс: ${resp.balance} ${resp.currency}.`,
                );
                setOperationOpen(false);
                loadMovements();
                loadByTeam();
            })
            .catch((err) => setOperationError(err))
            .finally(() => setOperationSubmitting(false));
    };

    const openBindAddress = (): void => {
        setBindAddressValue("");
        setBindAddressError("");
        setBindAddressOpen(true);
    };

    const confirmBindAddress = (): void => {
        if (!token) return;
        const address = bindAddressValue.trim();
        if (!TRON_ADDRESS_PATTERN.test(address)) {
            setBindAddressError("Введите TRON-адрес: 34 символа, начинается с T, без 0/O/I/l.");
            return;
        }
        setBindingAddress(true);
        setBindAddressError("");
        setMerchantDepositAddress(token, merchantId, address)
            .then((bound) => {
                setDepositAddresses((current) => [
                    bound,
                    ...current.filter((item) => item.id !== bound.id),
                ]);
                setBindAddressOpen(false);
                setSuccess("TRC20-адрес привязан. Переводы до момента привязки не зачисляются.");
            })
            .catch((err) => setBindAddressError(formatDepositAddressError(err)))
            .finally(() => setBindingAddress(false));
    };

    const confirmDisableAddress = (): void => {
        if (!token || !addressToDisable) return;
        setAddressLifecycleSubmitting(true);
        setAddressLifecycleError("");
        disableMerchantDepositAddress(token, merchantId, addressToDisable.id)
            .then(() => {
                setAddressToDisable(null);
                setShowDisabledAddresses(false);
                loadDepositAddresses();
                setSuccess("TRC20-адрес отключён. История и зачисления сохранены.");
            })
            .catch((err) => setAddressLifecycleError(formatDepositAddressError(err)))
            .finally(() => setAddressLifecycleSubmitting(false));
    };

    const confirmEnableAddress = (): void => {
        if (!token || !addressToEnable) return;
        const replaceCurrent = depositAddresses.some(
            (item) => item.enabled && item.id !== addressToEnable.id,
        );
        setAddressLifecycleSubmitting(true);
        setAddressLifecycleError("");
        enableMerchantDepositAddress(
            token,
            merchantId,
            addressToEnable.id,
            replaceCurrent,
        )
            .then(() => {
                setAddressToEnable(null);
                setShowDisabledAddresses(false);
                loadDepositAddresses();
                setSuccess(
                    replaceCurrent
                        ? "Активный TRC20-адрес заменён."
                        : "TRC20-адрес снова включён.",
                );
            })
            .catch((err) => setAddressLifecycleError(formatDepositAddressError(err)))
            .finally(() => setAddressLifecycleSubmitting(false));
    };

    const openBackfill = (): void => {
        setBackfillHours("24");
        setBackfillError("");
        setBackfillResult(null);
        setBackfillOpen(true);
    };

    const confirmBackfill = (): void => {
        if (!token) return;
        const hours = Number(backfillHours);
        if (!Number.isInteger(hours) || hours < 1 || hours > 72) {
            setBackfillError("Укажите целое окно от 1 до 72 часов.");
            return;
        }
        setBackfilling(true);
        setBackfillError("");
        setBackfillResult(null);
        backfillMerchantChainDeposits(token, merchantId, hours)
            .then((result) => {
                setBackfillResult(result);
                loadChainDeposits();
                loadMovements();
                getMerchant(token, merchantId).then(setMerchant).catch((err) => setError(err));
            })
            .catch((err) => setBackfillError(formatChainBackfillError(err)))
            .finally(() => setBackfilling(false));
    };

    const copyDepositAddress = async (address: string): Promise<void> => {
        try {
            await navigator.clipboard.writeText(address);
            setCopiedAddress(address);
            window.setTimeout(() => setCopiedAddress(""), 1500);
        } catch {
            setCopiedAddress("");
        }
    };

    const refreshAutoTopup = (): void => {
        loadDepositAddresses();
        loadChainDeposits();
        loadMovements();
        if (token) {
            getMerchant(token, merchantId).then(setMerchant).catch((err) => setError(err));
        }
    };

    const dismissSuccess = (): void => setSuccess("");

    // Статус «долг за лимитом» берём из серверного поля debtExceededBy: оно
    // свежее (не ждёт 30-секундного тика чекера) и посчитано бэкендом
    // (alert.ExcessAtomic) — правило «клиент деньги не считает» соблюдено.
    const debtExceededBy = merchant?.debtExceededBy ?? null;
    const overLimitActive = debtExceededBy != null;
    // Мерчант-зеркало: баланс читается из книги кабинета. Овердрафтная витрина к
    // нему не применяется — она про предоплату, которой здесь нет.
    const mirroredMerchant = Boolean(merchant && isMirroredMerchant(merchant));
    // Бронь и дробление заявок мерчанта, привязанного к кабинету, задаёт
    // площадка, а не эта карточка. Обоснование и цена ошибки — у предиката.
    const cabinetDrivenSettings = orderShapeComesFromCabinet(merchant);

    // «Спящих» настроек больше нет: порог предупреждения удалён (уведомление
    // в предоплате загорается фактом ухода в минус, ДМ 22.07), а лимит долга
    // действует в обоих режимах — в предоплате как допуск овердрафта.
    const resetDiscouraged = Boolean(
        merchant && !merchant.overdraftEnabled && isPositiveAmountString(merchant.amount),
    );
    // Кнопка «Вывести»: у долгового кабинета вывод легитимно уходит глубже в
    // минус, у остальных ограничен балансом. Условие и подпись — в cashFlow,
    // рядом с остальной денежной логикой формы и под тестами.
    const withdrawalButton = withdrawalButtonState(merchant);
    // Форма ручной операции. У долгового кабинета в ней нет свободной проводки:
    // сумму, направление и имя операции задаёт выбранная строка расхождения — см.
    // manualOperationFormState, там же и причина.
    const operationForm = manualOperationFormState({
        merchant,
        discrepancies,
        pickedKey: pickedDiscrepancy,
        amount: operationAmount,
        generatedKey: operationKey,
        submitting: operationSubmitting,
    });
    const merchantCurrency = merchant?.currency || "USDT";
    const trc20Address = depositAddresses.find(
        (item) => item.network === "trc20" && item.enabled,
    ) ?? null;
    const disabledTrc20Addresses = depositAddresses.filter(
        (item) => item.network === "trc20" && !item.enabled,
    );
    const enableWillReplace = Boolean(
        addressToEnable && trc20Address && addressToEnable.id !== trc20Address.id,
    );
    const movementsPageCount = Math.max(1, Math.ceil(movements.length / HISTORY_PAGE_SIZE));
    const visibleMovements = movements.slice(
        movementsPage * HISTORY_PAGE_SIZE,
        (movementsPage + 1) * HISTORY_PAGE_SIZE,
    );
    const chainDepositsPageCount = Math.max(1, Math.ceil(chainDeposits.length / HISTORY_PAGE_SIZE));
    const visibleChainDeposits = chainDeposits.slice(
        chainDepositsPage * HISTORY_PAGE_SIZE,
        (chainDepositsPage + 1) * HISTORY_PAGE_SIZE,
    );

    return (
        <div className="page merchant-details">
            <div className="page-header">
                <div className="merchant-details-title">
                    <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={onBack}
                    >
                        ← К списку
                    </button>
                    <h2>{merchant ? merchant.name : "Мерчант"}</h2>
                </div>
                {merchant && (
                    <div className="merchant-details-actions">
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={openEdit}
                        >
                            Редактировать
                        </button>
                        <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={rotateToken}
                            disabled={rotating}
                        >
                            {rotating ? "Ротация…" : "Сгенерировать токен"}
                        </button>
                        <div className="merchant-actions-menu" ref={actionsMenuRef}>
                            <button
                                className="btn btn-ghost btn-sm merchant-actions-trigger"
                                type="button"
                                aria-label="Ещё действия"
                                aria-haspopup="menu"
                                aria-expanded={actionsMenuOpen}
                                onClick={() => setActionsMenuOpen((prev) => !prev)}
                            >
                                ⋯
                            </button>
                            {actionsMenuOpen && (
                                <div className="merchant-actions-popover" role="menu">
                                    <button
                                        className="merchant-actions-item danger"
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setActionsMenuOpen(false);
                                            setRevokeError(null);
                                            setRevokeOpen(true);
                                        }}
                                    >
                                        Отозвать токен
                                    </button>
                                    {/* У предоплатного мерчанта с деньгами на балансе
                                        сброс — почти всегда не то, что нужно: он стирает
                                        предоплату вместо того, чтобы её вернуть, и не
                                        оставляет в леджере следа о выплате. Это UI-подсказка,
                                        а не запрет: контракт ручки не менялся, и прямой
                                        вызов API по-прежнему доступен (осознанный
                                        admin escape hatch). */}
                                    <button
                                        className="merchant-actions-item danger"
                                        type="button"
                                        role="menuitem"
                                        disabled={resetDiscouraged}
                                        title={
                                            resetDiscouraged
                                                ? "У мерчанта на предоплате есть баланс — используйте вывод, чтобы он попал в ДДС"
                                                : undefined
                                        }
                                        onClick={() => {
                                            setActionsMenuOpen(false);
                                            setResetError(null);
                                            setResetOpen(true);
                                        }}
                                    >
                                        Сбросить баланс
                                    </button>
                                    <button
                                        className="merchant-actions-item danger"
                                        type="button"
                                        role="menuitem"
                                        onClick={() => {
                                            setActionsMenuOpen(false);
                                            setArchiveError(null);
                                            setArchiveOpen(true);
                                        }}
                                    >
                                        Архивировать
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>

            {success && (
                <div className="alert alert-info">
                    {success}{" "}
                    <button
                        className="alert-dismiss"
                        type="button"
                        onClick={dismissSuccess}
                    >
                        ×
                    </button>
                </div>
            )}
            <ErrorBanner error={error} title="Не удалось загрузить мерчанта" />
            <ErrorBanner error={rotateError} title="Не удалось сгенерировать токен" />

            {loading && <div className="empty-state">Загрузка мерчанта…</div>}

            <ErrorBanner error={resetError} title="Merchant balance was not reset" />

            {merchant && !loading && (
                <section className="merchant-details-card">
                    <section className="merchant-debt-block">
                        <div className="section-header">
                            <h3>Расчёты и долг</h3>
                            <div className="section-header-actions">
                                <button
                                    className="btn btn-ghost btn-sm"
                                    type="button"
                                    onClick={() => openOperation("topup")}
                                >
                                    Пополнить
                                </button>
                                <button
                                    className="btn btn-ghost btn-sm"
                                    type="button"
                                    onClick={() => openOperation("withdrawal")}
                                    disabled={withdrawalButton.disabled}
                                    title={withdrawalButton.title ?? undefined}
                                >
                                    Вывести
                                </button>
                                <button
                                    className="btn btn-ghost btn-sm"
                                    type="button"
                                    onClick={openMode}
                                >
                                    Настроить режим
                                </button>
                            </div>
                        </div>
                        <div className="merchant-debt-metrics">
                            <div className="debt-metric">
                                <span className="meta-label">Баланс</span>
                                <span className="meta-value mono debt-balance">
                                    {formatMerchantBalance(merchant)}
                                </span>
                            </div>
                            <div className="debt-metric">
                                <span className="meta-label">Режим расчётов</span>
                                <span className="meta-value debt-mode">
                                    {formatSettlementMode(merchant)}
                                </span>
                                <span className="debt-metric-sub">
                                    {formatSettlementSetting(merchant)}
                                </span>
                            </div>
                            {/* У мерчанта-зеркала лампы долга нет: она меряет долг
                                сверх лимита, а здесь ни долга, ни лимита не
                                существует — баланс принадлежит кабинету на
                                стороне площадки. Состояние такого мерчанта
                                показывает сверка в блоке «Книга кабинета». */}
                            {!mirroredMerchant && (
                                <div className="debt-metric">
                                    <span className="meta-label">Статус</span>
                                    <span
                                        className={`merchant-debt-value ${overLimitActive ? "over-limit" : ""}`}
                                    >
                                        {overLimitActive ? "Долг за лимитом" : "В норме"}
                                    </span>
                                    <span className="debt-metric-sub">
                                        {overLimitActive
                                            ? `превышен на ${debtExceededBy} USDT · трейдеры видят метку «Долг», взятие не блокируется`
                                            : "заявки выдаются трейдерам"}
                                    </span>
                                </div>
                            )}
                        </div>
                    </section>

                    <section className="merchant-settings-block">
                        <div className="section-header"><h3>Основные настройки</h3></div>
                    <div className="merchant-settings-grid">
                        <div className="meta-card">
                            <span className="meta-label">Имя</span>
                            <span className="meta-value">{merchant.name}</span>
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Платежей на заявку</span>
                            <span className="meta-value">{merchant.maxPaymentsPerOrder}</span>
                            {cabinetDrivenSettings && (
                                <span className="meta-note">
                                    не применяется: заявки кабинета дробятся без лимита,
                                    сколько платежей делать — решает трейдер
                                </span>
                            )}
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">Срок заявки, мин</span>
                            <span className="meta-value">{merchant.claimHoldMinutes}</span>
                            {cabinetDrivenSettings ? (
                                <span className="meta-note">
                                    не применяется: срок приходит из кабинета площадки,
                                    свой у каждой заявки
                                </span>
                            ) : (
                                <span className="meta-note">
                                    считается от создания заявки: взятие срок не двигает,
                                    невзятая заявка закрывается сама
                                </span>
                            )}
                        </div>
                        <div className="meta-card">
                            <span className="meta-label">VIP</span>
                            <span className={`meta-value merchant-vip-value ${merchant.isVip ? "active" : ""}`}>
                                {merchant.isVip ? "VIP" : "Нет"}
                            </span>
                        </div>
                    </div>

                        <div className="merchant-technical-details">
                            <span>ID: <code>{merchant.id}</code></span>
                            <span>Balance ID: <code>{merchant.balanceId}</code></span>
                        </div>
                    </section>

                    <div className="merchant-access-rate-grid">
                    <section className="merchant-rate-block merchant-portal-access-block">
                        <div className="section-header">
                            <div>
                                <h3>Доступ в личный кабинет</h3>
                                <p className="section-hint">
                                    Один portal user на мерчанта; вход с нескольких устройств разрешён.
                                    Сброс пароля и отключение завершают все активные сессии.
                                </p>
                            </div>
                            {portalAccess && (
                                <div className="section-header-actions">
                                    <button
                                        className="btn btn-primary btn-sm"
                                        type="button"
                                        onClick={openPortalCredential}
                                    >
                                        {portalAccess.enabled ? "Сбросить пароль" : "Выдать доступ"}
                                    </button>
                                    {portalAccess.enabled && (
                                        <button
                                            className="btn btn-danger btn-sm"
                                            type="button"
                                            onClick={() => {
                                                setPortalDisableError(null);
                                                setPortalDisableOpen(true);
                                            }}
                                        >
                                            Отключить доступ
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                        <ErrorBanner error={portalAccessError} title="Статус доступа не загружен" />
                        {portalAccessLoading && <div className="empty-state compact">Загрузка доступа…</div>}
                        {portalAccess && !portalAccessLoading && (
                            <div className="trader-meta-grid">
                                <div className="meta-card">
                                    <span className="meta-label">Статус</span>
                                    <span className={`meta-value merchant-vip-value ${portalAccess.enabled ? "active" : ""}`}>
                                        {portalAccess.enabled ? "Доступ включён" : "Доступ отключён"}
                                    </span>
                                </div>
                                <div className="meta-card">
                                    <span className="meta-label">Логин</span>
                                    <span className="meta-value mono">{portalAccess.login || "—"}</span>
                                </div>
                                <div className="meta-card">
                                    <span className="meta-label">Обновлён</span>
                                    <span className="meta-value">
                                        {portalAccess.updatedAt ? formatDate(portalAccess.updatedAt) : "—"}
                                    </span>
                                </div>
                                <div className="meta-card">
                                    <span className="meta-label">2FA</span>
                                    <span className="meta-value">
                                        <SecondFactorControl
                                            userId={portalAccess.enabled ? portalAccess.userId : undefined}
                                            login={portalAccess.login ?? ""}
                                            status={portalAccess.secondFactor}
                                            onChanged={(secondFactor) =>
                                                setPortalAccess((current) => (current ? {...current, secondFactor} : current))
                                            }
                                        />
                                    </span>
                                </div>
                            </div>
                        )}
                    </section>

                    <MerchantRatesSection token={token!} merchant={merchant} onSaved={(updated) => {
                        setMerchant(updated);
                        setSuccess("Настройки курса обновлены. Уже созданные заявки сохраняют свой курс.");
                    }}/>


                    </div>

                    <ExternalMerchantSection
                        merchant={merchant}
                        token={token}
                        onChanged={setMerchant}
                    />

                    <CabinetBookSection merchant={merchant} token={token} onSettled={(message) => {
                        setSuccess(message);
                        if (token) getMerchant(token, merchantId).then(setMerchant).catch(setError);
                        loadMovements();
                        loadByTeam();
                    }} />

                    {/* Раздел живёт рядом с книгой кабинета намеренно: в книге
                        видно, что площадка кабинету заплатила, а здесь — что
                        мы этого у себя не записали. */}
                    <PlatformClosedOrdersSection
                        merchant={merchant}
                        token={token}
                        onSettled={(message) => {
                            setSuccess(message);
                            if (token) {
                                getMerchant(token, merchantId).then(setMerchant).catch((err) => setError(err));
                            }
                            loadMovements();
                            loadByTeam();
                        }}
                    />

                    <MerchantSellAddressesSection key={merchantId} merchantId={merchantId} token={token}/>

                    <section className="merchant-chain-topup-section">
                        <div className="section-header">
                            <div>
                                <h3>Автопополнение (TRC20)</h3>
                                <p className="section-hint">
                                    Watch-only: система читает подтверждённые USDT-переводы, но не хранит ключи и не отправляет средства.
                                </p>
                            </div>
                            <div className="section-header-actions">
                                <button
                                    className="btn btn-ghost btn-sm"
                                    type="button"
                                    onClick={refreshAutoTopup}
                                    disabled={depositAddressesLoading || chainDepositsLoading}
                                >
                                    {depositAddressesLoading || chainDepositsLoading ? "Обновление…" : "Обновить"}
                                </button>
                                {!trc20Address && (
                                    <button
                                        className="btn btn-primary btn-sm"
                                        type="button"
                                        onClick={openBindAddress}
                                    >
                                        Привязать адрес
                                    </button>
                                )}
                            </div>
                        </div>

                        {depositAddressesError && <div className="alert alert-error">{depositAddressesError}</div>}
                        {chainDepositsError && <div className="alert alert-error">{chainDepositsError}</div>}

                        {trc20Address ? (
                            <div className="merchant-chain-address-card">
                                <div>
                                    <div className="merchant-chain-address-status">
                                        <span className="meta-label">Текущий адрес</span>
                                        <span className="badge badge-in">Активен</span>
                                    </div>
                                    <div className="mono merchant-chain-address" title={trc20Address.address}>
                                        {trc20Address.address}
                                    </div>
                                    <span className="muted">Включён {formatDate(trc20Address.activeFrom)}</span>
                                </div>
                                <div className="merchant-chain-address-actions">
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        type="button"
                                        onClick={() => void copyDepositAddress(trc20Address.address)}
                                    >
                                        {copiedAddress === trc20Address.address ? "Скопировано" : "Копировать"}
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        type="button"
                                        onClick={openBackfill}
                                    >
                                        Восстановить пропуски
                                    </button>
                                    <button
                                        className="btn btn-ghost btn-sm"
                                        type="button"
                                        onClick={() => {
                                            setAddressLifecycleError("");
                                            setAddressToDisable(trc20Address);
                                        }}
                                    >
                                        Отключить
                                    </button>
                                </div>
                            </div>
                        ) : (
                            !depositAddressesLoading && (
                                <div className="empty-state compact">
                                    {disabledTrc20Addresses.length > 0
                                        ? "Автопополнение отключено. Новые TRC20-переводы сейчас не зачисляются."
                                        : "TRC20-адрес ещё не привязан. История переводов до привязки не зачисляется."}
                                </div>
                            )
                        )}

                        {disabledTrc20Addresses.length > 0 && (
                            <div className="merchant-chain-disabled">
                                <button
                                    className="btn btn-ghost btn-sm merchant-chain-disabled-toggle"
                                    type="button"
                                    aria-expanded={showDisabledAddresses}
                                    onClick={() => setShowDisabledAddresses((shown) => !shown)}
                                >
                                    {showDisabledAddresses ? "Скрыть" : "Показать"} отключённые адреса
                                    {" "}({disabledTrc20Addresses.length})
                                </button>
                                {showDisabledAddresses && (
                                    <div className="merchant-chain-disabled-list">
                                        {disabledTrc20Addresses.map((address) => (
                                            <div
                                                className="merchant-chain-disabled-row"
                                                key={address.id}
                                            >
                                                <div>
                                                    <div className="merchant-chain-address-status">
                                                        <span className="meta-label">Отключённый адрес</span>
                                                        <span className="badge">Отключён</span>
                                                    </div>
                                                    <div
                                                        className="mono merchant-chain-address"
                                                        title={address.address}
                                                    >
                                                        {address.address}
                                                    </div>
                                                    <span className="muted">
                                                        Отключён{" "}
                                                        {address.disabledAt
                                                            ? formatDate(address.disabledAt)
                                                            : "—"}
                                                    </span>
                                                </div>
                                                <div className="merchant-chain-address-actions">
                                                    <button
                                                        className="btn btn-ghost btn-sm"
                                                        type="button"
                                                        onClick={() => void copyDepositAddress(address.address)}
                                                    >
                                                        {copiedAddress === address.address
                                                            ? "Скопировано"
                                                            : "Копировать"}
                                                    </button>
                                                    <button
                                                        className="btn btn-primary btn-sm"
                                                        type="button"
                                                        onClick={() => {
                                                            setAddressLifecycleError("");
                                                            setAddressToEnable(address);
                                                        }}
                                                    >
                                                        Включить
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <h4 className="merchant-chain-history-title">Последние автопополнения</h4>
                        <div className="table-wrapper">
                            <table className="merchant-chain-deposits-table">
                                <thead>
                                <tr>
                                    <th>Время</th>
                                    <th>Сумма</th>
                                    <th>From</th>
                                    <th>Tx</th>
                                </tr>
                                </thead>
                                <tbody>
                                {visibleChainDeposits.map((deposit) => (
                                    <tr key={`${deposit.txId}:${deposit.eventIndex}`}>
                                        <td className="mono" title={`Зачислено ${formatDate(deposit.creditedAt)}`}>
                                            {formatDate(deposit.blockTimestamp)}
                                        </td>
                                        <td className="mono balance-delta positive">{deposit.amount} USDT</td>
                                        <td className="mono" title={deposit.from}>{shortChainValue(deposit.from)}</td>
                                        <td>
                                            <a
                                                className="mono"
                                                href={merchantChainDepositExplorerUrl(deposit)}
                                                target="_blank"
                                                rel="noreferrer"
                                                title={deposit.txId}
                                            >
                                                {shortChainValue(deposit.txId)}
                                            </a>
                                        </td>
                                    </tr>
                                ))}
                                {!chainDepositsLoading && chainDeposits.length === 0 && (
                                    <tr>
                                        <td colSpan={4}>
                                            <div className="empty-state compact">Автопополнений пока нет.</div>
                                        </td>
                                    </tr>
                                )}
                                {chainDepositsLoading && chainDeposits.length === 0 && (
                                    <tr>
                                        <td colSpan={4}>
                                            <div className="empty-state compact">Загрузка автопополнений…</div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                        <HistoryPagination
                            page={chainDepositsPage}
                            pageCount={chainDepositsPageCount}
                            total={chainDeposits.length}
                            onPageChange={setChainDepositsPage}
                            label="Пагинация автопополнений"
                            testId="chain-deposits-pagination"
                        />
                    </section>

                    <section className="balance-by-merchant-section">
                        <div className="section-header">
                            <h3>По командам</h3>
                        </div>
                        <ErrorBanner error={byTeamError} title="Не удалось загрузить разбивку по командам" />
                        <div className="table-wrapper">
                            <table className="balance-by-merchant-table">
                                <thead>
                                <tr>
                                    <th>Команда</th>
                                    <th>Баланс</th>
                                </tr>
                                </thead>
                                <tbody>
                                {byTeam?.items.map((item) => (
                                    <tr key={item.teamId}>
                                        <td>{item.teamName}</td>
                                        <td className={`mono balance-delta ${item.amount.trim().startsWith("-") ? "negative" : "positive"}`}>
                                            {item.amount} {byTeam.currency}
                                        </td>
                                    </tr>
                                ))}
                                {byTeam?.unattributed != null && (
                                    <tr>
                                        <td>Без команды</td>
                                        <td className="mono balance-delta negative">
                                            {byTeam.unattributed} {byTeam.currency}
                                        </td>
                                    </tr>
                                )}
                                {!byTeamLoading && byTeam && byTeam.items.length === 0 && byTeam.unattributed == null && (
                                    <tr>
                                        <td colSpan={2}>
                                            <div className="empty-state compact">Долгов по командам нет.</div>
                                        </td>
                                    </tr>
                                )}
                                {byTeamLoading && !byTeam && (
                                    <tr>
                                        <td colSpan={2}>
                                            <div className="empty-state compact">Загрузка разбивки...</div>
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <BalanceMovementsTable
                        items={visibleMovements}
                        currency={movementsCurrency || merchant.currency}
                        loading={movementsLoading}
                        error={movementsError}
                        hasMore={false}
                        onLoadMore={() => undefined}
                        counterpartyLabel="Команда"
                        footer={(
                            <HistoryPagination
                                page={movementsPage}
                                pageCount={movementsPageCount}
                                total={movements.length}
                                onPageChange={setMovementsPage}
                                label="Пагинация движений"
                                testId="balance-movements-pagination"
                            />
                        )}
                    />

                    <section className="merchant-notifications-section">
                        <div className="section-header">
                            <h3>Уведомления</h3>
                            <button
                                className="btn btn-ghost btn-sm"
                                type="button"
                                onClick={loadNotifications}
                                disabled={notificationsLoading}
                            >
                                {notificationsLoading ? "Обновление…" : "Обновить"}
                            </button>
                        </div>
                        <ErrorBanner error={notificationsError} title="Не удалось загрузить уведомления" />
                        <div className="table-wrapper">
                            <table className="merchant-notifications-table">
                                <thead>
                                <tr>
                                    <th>Когда</th>
                                    <th>Тип</th>
                                    <th>Текст</th>
                                    <th>Статус</th>
                                    <th />
                                </tr>
                                </thead>
                                <tbody>
                                {notifications.length === 0 && !notificationsLoading && (
                                    <tr>
                                        <td colSpan={5}>
                                            <div className="empty-state compact">Уведомлений нет.</div>
                                        </td>
                                    </tr>
                                )}
                                {notifications.map((n) => (
                                    <tr
                                        key={n.id}
                                        className={n.status === "active" ? `notification-row severity-${n.severity}` : "notification-row resolved"}
                                    >
                                        <td className="mono">{formatDate(n.createdAt)}</td>
                                        <td>{notificationTypeLabel(n.type)}</td>
                                        <td>{n.body}</td>
                                        <td>
                                            <span className={`notification-status-pill status-${n.status}`}>
                                                {n.status === "active" ? "Активно" : "Закрыто"}
                                            </span>
                                            {n.status === "active" && !n.readAt && (
                                                <span className="notification-unread-dot" title="Не прочитано" />
                                            )}
                                        </td>
                                        <td>
                                            {n.status === "active" && !n.readAt && (
                                                <button
                                                    className="btn btn-ghost btn-sm"
                                                    type="button"
                                                    onClick={() => markNotificationAsRead(n.id)}
                                                >
                                                    Прочитано
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </section>
            )}

            <Modal
                open={bindAddressOpen}
                title="Привязать TRC20-адрес"
                onClose={() => {
                    if (!bindingAddress) setBindAddressOpen(false);
                }}
                disableClose={bindingAddress}
                sizeClassName="modal-sm"
            >
                {bindAddressError && <div className="alert alert-error">{bindAddressError}</div>}
                <div className="alert alert-warning">
                    Адрес должен быть создан бизнесом. Система только читает блокчейн и не хранит приватный ключ.
                    Переводы, сделанные до привязки, не зачисляются.
                </div>
                <label className="field">
                    <span>TRON-адрес</span>
                    <input
                        type="text"
                        className="mono"
                        value={bindAddressValue}
                        onChange={(event) => {
                            setBindAddressValue(event.target.value);
                            setBindAddressError("");
                        }}
                        placeholder="T…"
                        autoComplete="off"
                        spellCheck={false}
                        autoFocus
                    />
                    <small>USDT TRC20, Base58Check, 34 символа.</small>
                </label>
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setBindAddressOpen(false)}
                        disabled={bindingAddress}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={confirmBindAddress}
                        disabled={bindingAddress || bindAddressValue.trim() === ""}
                    >
                        {bindingAddress ? "Привязка…" : "Подтвердить привязку"}
                    </button>
                </div>
            </Modal>

            <Modal
                open={backfillOpen}
                title="Восстановить пропущенные TRC20-переводы"
                onClose={() => {
                    if (!backfilling) setBackfillOpen(false);
                }}
                disableClose={backfilling}
                sizeClassName="modal-sm"
            >
                {backfillError && <div className="alert alert-error">{backfillError}</div>}
                <div className="alert alert-warning">
                    Повторно проверяются подтверждённые переводы на уже привязанный адрес. Операция
                    идемпотентна: ранее зачисленные события будут отмечены как дубликаты.
                </div>
                <label className="field">
                    <span>Окно, часов</span>
                    <input
                        type="number"
                        min={1}
                        max={72}
                        step={1}
                        value={backfillHours}
                        onChange={(event) => {
                            setBackfillHours(event.target.value);
                            setBackfillError("");
                            setBackfillResult(null);
                        }}
                        disabled={backfilling}
                    />
                    <small>От 1 до 72 часов назад от текущего момента.</small>
                </label>
                {backfillResult && (
                    <div className="alert alert-info" data-testid="chain-backfill-result">
                        Найдено: {backfillResult.found}; зачислено: {backfillResult.credited};
                        дубликатов: {backfillResult.duplicates}; пропущено: {backfillResult.skipped}.
                    </div>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setBackfillOpen(false)}
                        disabled={backfilling}
                    >
                        Закрыть
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={confirmBackfill}
                        disabled={backfilling || backfillHours.trim() === ""}
                    >
                        {backfilling ? "Проверка…" : "Запустить backfill"}
                    </button>
                </div>
            </Modal>

            <Modal
                open={addressToDisable !== null}
                title="Отключить TRC20-адрес?"
                onClose={() => {
                    if (!addressLifecycleSubmitting) setAddressToDisable(null);
                }}
                disableClose={addressLifecycleSubmitting}
                sizeClassName="modal-sm"
            >
                {addressLifecycleError && (
                    <div className="alert alert-error">{addressLifecycleError}</div>
                )}
                <p className="modal-hint">
                    Переводы после момента отключения не будут зачисляться. Перевод, совершённый раньше,
                    может появиться после подтверждения. Ledger, история и сам адрес сохранятся — его
                    можно будет включить снова.
                </p>
                {addressToDisable && (
                    <div className="token-merchant-id mono">{addressToDisable.address}</div>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setAddressToDisable(null)}
                        disabled={addressLifecycleSubmitting}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-danger"
                        type="button"
                        onClick={confirmDisableAddress}
                        disabled={addressLifecycleSubmitting}
                    >
                        {addressLifecycleSubmitting ? "Отключение…" : "Отключить адрес"}
                    </button>
                </div>
            </Modal>

            <Modal
                open={addressToEnable !== null}
                title={enableWillReplace ? "Сменить активный TRC20-адрес?" : "Включить TRC20-адрес?"}
                onClose={() => {
                    if (!addressLifecycleSubmitting) setAddressToEnable(null);
                }}
                disableClose={addressLifecycleSubmitting}
                sizeClassName="modal-sm"
            >
                {addressLifecycleError && (
                    <div className="alert alert-error">{addressLifecycleError}</div>
                )}
                <p className="modal-hint">
                    {enableWillReplace
                        ? "Текущий адрес будет отключён, а выбранный включён атомарно. В каждый момент у мерчанта остаётся только один активный TRC20-адрес."
                        : "Для адреса начнётся новый период активности. Переводы, совершённые во время отключения, не будут зачислены."}
                </p>
                {enableWillReplace && trc20Address && (
                    <div className="merchant-chain-address-replacement">
                        <span className="muted">Сейчас</span>
                        <div className="token-merchant-id mono">{trc20Address.address}</div>
                        <span className="muted">Будет включён</span>
                    </div>
                )}
                {addressToEnable && (
                    <div className="token-merchant-id mono">{addressToEnable.address}</div>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setAddressToEnable(null)}
                        disabled={addressLifecycleSubmitting}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={confirmEnableAddress}
                        disabled={addressLifecycleSubmitting}
                    >
                        {addressLifecycleSubmitting
                            ? "Включение…"
                            : enableWillReplace
                                ? "Сменить адрес"
                                : "Включить адрес"}
                    </button>
                </div>
            </Modal>

            {/* ─── Edit modal ─── */}
            <Modal
                open={editOpen}
                title="Редактирование мерчанта"
                onClose={() => setEditOpen(false)}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={editError} />
                <p className="modal-hint">
                    Заполняйте только то, что нужно изменить. Пустое поле = без изменений.
                </p>
                <div className="form-group">
                    <label htmlFor="edit-max-payments">
                        Max payments per order ({MAX_PAYMENTS_MIN}–{MAX_PAYMENTS_MAX})
                    </label>
                    <input
                        id="edit-max-payments"
                        type="number"
                        min={MAX_PAYMENTS_MIN}
                        max={MAX_PAYMENTS_MAX}
                        step={1}
                        value={editMaxPayments}
                        onChange={(e) => setEditMaxPayments(e.target.value)}
                        disabled={cabinetDrivenSettings}
                        aria-describedby={
                            cabinetDrivenSettings ? "edit-max-payments-hint" : undefined
                        }
                        placeholder={
                            merchant
                                ? `текущее: ${merchant.maxPaymentsPerOrder}`
                                : "без изменений"
                        }
                    />
                    {cabinetDrivenSettings && (
                        <p className="modal-hint" id="edit-max-payments-hint">
                            У этого мерчанта заявки приходят из кабинета площадки и дробятся
                            без лимита: сколько платежей сделать, решает трейдер. Настройка
                            на его заявки не влияет.
                        </p>
                    )}
                </div>
                <div className="form-group">
                    <label htmlFor="edit-claim-hold">
                        Срок заявки, минут (от {CLAIM_HOLD_MIN})
                    </label>
                    <input
                        id="edit-claim-hold"
                        type="number"
                        min={CLAIM_HOLD_MIN}
                        step={1}
                        value={editClaimHold}
                        onChange={(e) => setEditClaimHold(e.target.value)}
                        disabled={cabinetDrivenSettings}
                        aria-describedby={
                            cabinetDrivenSettings ? "edit-claim-hold-hint" : undefined
                        }
                        placeholder={
                            merchant
                                ? `текущее: ${merchant.claimHoldMinutes}`
                                : "без изменений"
                        }
                    />
                    {cabinetDrivenSettings && (
                        <p className="modal-hint" id="edit-claim-hold-hint">
                            У этого мерчанта срок приходит из кабинета площадки — свой у
                            каждой заявки. Настройка на его заявки не влияет: ни как
                            потолок, ни как пол.
                        </p>
                    )}
                </div>
                <label className="toggle-row" htmlFor="edit-is-vip">
                    <input
                        id="edit-is-vip"
                        type="checkbox"
                        checked={editIsVip}
                        onChange={(e) => setEditIsVip(e.target.checked)}
                    />
                    <span>
                        <strong>VIP-мерчант</strong>
                        <small>Заявки поднимаются выше в пуле трейдера.</small>
                    </span>
                </label>
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setEditOpen(false)}
                        disabled={editSaving}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={submitEdit}
                        disabled={editSaving}
                    >
                        {editSaving ? "Сохранение…" : "Сохранить"}
                    </button>
                </div>
            </Modal>

            {/* ─── Settlement mode modal ─── */}
            <Modal
                open={modeOpen}
                title="Режим расчётов"
                onClose={() => setModeOpen(false)}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={modeError} />
                <div
                    className="mode-radio-group"
                    role="radiogroup"
                    aria-label="Режим расчётов"
                >
                    <label
                        className={`mode-radio-card ${modeEnabled ? "selected" : ""}`}
                        htmlFor="mode-overdraft"
                    >
                        <input
                            id="mode-overdraft"
                            type="radio"
                            name="settlement-mode"
                            checked={modeEnabled}
                            onChange={() => setModeEnabled(true)}
                        />
                        <span>
                            <strong>Овердрафт — работа в долг</strong>
                            <small>Долг без блокировок; при превышении лимита — уведомление.</small>
                        </span>
                    </label>
                    <label
                        className={`mode-radio-card ${!modeEnabled ? "selected" : ""}`}
                        htmlFor="mode-prepaid"
                    >
                        <input
                            id="mode-prepaid"
                            type="radio"
                            name="settlement-mode"
                            checked={!modeEnabled}
                            onChange={() => setModeEnabled(false)}
                        />
                        <span>
                            <strong>Предоплата — работа с баланса</strong>
                            <small>
                                Уведомление при уходе баланса в минус; блок взятия —
                                когда исчерпан допуск овердрафта.
                            </small>
                        </span>
                    </label>
                </div>

                {/* Одно поле на оба режима: в БД это одна настройка
                    (overdraftLimit). В овердрафте — сигнальный лимит долга,
                    в предоплате — допуск, на сколько можно уйти в минус. */}
                <div className="form-group mode-field">
                    <label htmlFor="mode-overdraft-limit">
                        {modeEnabled ? "Лимит долга" : "Допуск овердрафта"},{" "}
                        {merchantCurrency}
                    </label>
                    <input
                        id="mode-overdraft-limit"
                        type="text"
                        inputMode="decimal"
                        value={modeLimit}
                        onChange={(e) => setModeLimit(e.target.value)}
                        placeholder={modeEnabled ? "пусто = без лимита" : "пусто = допуск 0"}
                    />
                    {modeEnabled ? (
                        <small className="field-hint">
                            При превышении лимита {modeLimit.trim() || "N"} {merchantCurrency}
                            {" "}(баланс ниже −{modeLimit.trim() || "N"}) придёт критическое
                            уведомление в админку, а у трейдеров на заявках появится метка
                            «Долг». Взятие заявок не блокируется. Пусто = долг без лимита.
                        </small>
                    ) : (
                        <small className="field-hint">
                            Вводится положительным: допуск {modeLimit.trim() || "N"}{" "}
                            {merchantCurrency} разрешает работать до баланса
                            −{modeLimit.trim() || "N"}. Уведомление придёт при уходе
                            баланса в минус, а когда заявка не влезает в остаток
                            допуска — её нельзя взять в работу. Пусто = строго до нуля.
                        </small>
                    )}
                </div>

                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setModeOpen(false)}
                        disabled={modeSaving}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={submitMode}
                        disabled={modeSaving}
                    >
                        {modeSaving ? "Сохранение…" : "Сохранить"}
                    </button>
                </div>
            </Modal>

            <Modal
                open={portalCredentialOpen}
                title={portalAccess?.enabled ? "Сбросить пароль личного кабинета" : "Выдать доступ в личный кабинет"}
                onClose={closePortalCredential}
                disableClose={portalCredentialSaving}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={portalCredentialError} title="Доступ не обновлён" />
                {portalAccess?.enabled && (
                    <div className="alert alert-warning">
                        После сохранения все текущие browser-сессии мерчанта будут завершены.
                    </div>
                )}
                <div className="form-group">
                    <label htmlFor="portal-access-login">Логин</label>
                    <input
                        id="portal-access-login"
                        type="text"
                        value={portalLogin}
                        onChange={(e) => setPortalLogin(e.target.value)}
                        autoComplete="off"
                        spellCheck={false}
                    />
                    <small>3–100 символов: латиница, цифры, точка, дефис или подчёркивание.</small>
                </div>
                <div className="form-group">
                    <label htmlFor="portal-access-password">Новый пароль</label>
                    <input
                        id="portal-access-password"
                        type={portalPasswordVisible ? "text" : "password"}
                        value={portalPassword}
                        onChange={(e) => setPortalPassword(e.target.value)}
                        autoComplete="new-password"
                    />
                    <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        onClick={() => {
                            const generated = generateMerchantPortalPassword();
                            setPortalPassword(generated);
                            setPortalPasswordConfirmation(generated);
                            setPortalPasswordVisible(true);
                        }}
                    >
                        Сгенерировать безопасный пароль
                    </button>
                    <button
                        className="btn btn-ghost btn-sm"
                        type="button"
                        aria-controls="portal-access-password portal-access-password-confirmation"
                        aria-pressed={portalPasswordVisible}
                        onClick={() => setPortalPasswordVisible((visible) => !visible)}
                    >
                        {portalPasswordVisible ? "Скрыть пароль" : "Показать пароль"}
                    </button>
                    <small>Не менее 15 символов; максимум 72 байта.</small>
                </div>
                <div className="form-group">
                    <label htmlFor="portal-access-password-confirmation">Повторите пароль</label>
                    <input
                        id="portal-access-password-confirmation"
                        type={portalPasswordVisible ? "text" : "password"}
                        value={portalPasswordConfirmation}
                        onChange={(e) => setPortalPasswordConfirmation(e.target.value)}
                        autoComplete="new-password"
                    />
                </div>
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={closePortalCredential}
                        disabled={portalCredentialSaving}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={submitPortalCredential}
                        disabled={portalCredentialSaving}
                    >
                        {portalCredentialSaving ? "Сохранение…" : "Сохранить"}
                    </button>
                </div>
            </Modal>

            <Modal
                open={portalDisableOpen}
                title="Отключить личный кабинет?"
                onClose={closePortalDisable}
                disableClose={portalDisabling}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={portalDisableError} title="Доступ не отключён" />
                <p className="modal-hint">
                    Portal user станет неактивным. Все JWT-сессии и refresh-токены
                    будут немедленно отозваны. Статический API-токен мерчанта не изменится.
                </p>
                {portalAccess?.login && (
                    <div className="token-merchant-id mono">{portalAccess.login}</div>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={closePortalDisable}
                        disabled={portalDisabling}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-danger"
                        type="button"
                        onClick={confirmPortalDisable}
                        disabled={portalDisabling}
                    >
                        {portalDisabling ? "Отключение…" : "Отключить доступ"}
                    </button>
                </div>
            </Modal>

            {/* ─── Rotate token: show-once ─── */}
            <Modal
                open={tokenResp !== null}
                title="Новый API-токен мерчанта"
                onClose={closeTokenModal}
                disableClose={!tokenCopied}
            >
                {tokenResp && (
                    <>
                        <div className="alert alert-error token-warning">
                            <strong>Внимание: токен показан только один раз.</strong> Прежний
                            токен уже инвалидирован. Скопируйте новый токен сейчас — повторно
                            он недоступен.
                        </div>
                        <div className="merchant-token-hint">
                            Система хранит только SHA-256-хэш токена — восстановить его позже
                            нельзя, можно лишь сгенерировать заново или отозвать.
                        </div>
                        <div className="form-group">
                            <label>Merchant ID</label>
                            <div className="mono token-merchant-id">
                                {tokenResp.merchantId}
                            </div>
                        </div>
                        <div className="form-group">
                            <label>Токен</label>
                            <div className="token-value mono">{tokenResp.token}</div>
                        </div>
                        <div className="modal-actions">
                            <button
                                className="btn btn-primary"
                                type="button"
                                onClick={copyToken}
                            >
                                {tokenCopied ? "✓ Скопировано" : "Копировать токен"}
                            </button>
                            <button
                                className="btn btn-ghost"
                                type="button"
                                onClick={closeTokenModal}
                                disabled={!tokenCopied}
                                title={tokenCopied ? undefined : "Сначала скопируйте токен"}
                            >
                                Я сохранил, закрыть
                            </button>
                        </div>
                    </>
                )}
            </Modal>

            {/* ─── Revoke confirm ─── */}
            <Modal
                open={revokeOpen}
                title="Отзыв API-токена"
                onClose={() => setRevokeOpen(false)}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={revokeError} />
                <p className="modal-hint">
                    Действующий токен мерчанта будет немедленно инвалидирован. Мерчант
                    перестанет принимать заявки, пока вы не сгенерируете новый токен.
                    Продолжить?
                </p>
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setRevokeOpen(false)}
                        disabled={revoking}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-danger"
                        type="button"
                        onClick={confirmRevoke}
                        disabled={revoking}
                    >
                        {revoking ? "Отзыв…" : "Отозвать токен"}
                    </button>
                </div>
            </Modal>

            {/* ДДС: ручное пополнение / вывод */}
            <Modal
                open={operationOpen}
                title={operationType === "topup" ? "Пополнение баланса мерчанта" : "Вывод с баланса мерчанта"}
                onClose={() => {
                    if (!operationSubmitting) setOperationOpen(false);
                }}
                disableClose={operationSubmitting}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={operationError} />
                {merchant && (
                    <>
                        <p className="modal-hint">
                            {operationType === "topup"
                                ? "Деньги, поступившие от мерчанта. Запишется одной строкой в леджер и увеличит баланс."
                                : "Деньги, возвращённые мерчанту. Запишется одной строкой в леджер и уменьшит баланс."}
                        </p>
                        <div className="confirm-summary">
                            <div>
                                <span>Текущий баланс</span>
                                <strong className="mono">{formatMerchantBalance(merchant)}</strong>
                            </div>
                            {availability && (
                                <div>
                                    <span>В работе у трейдеров</span>
                                    <strong className="mono">
                                        {availability.hasUnknownInFlight ? "≥ " : "~ "}
                                        {availability.knownInFlight} {availability.currency}
                                    </strong>
                                </div>
                            )}
                            {availability && !availability.overdraftEnabled && (
                                <div>
                                    <span>Доступно для взятия заявок</span>
                                    <strong className="mono">
                                        {availability.available} {availability.currency}
                                    </strong>
                                </div>
                            )}
                        </div>
                        {availabilityError && (
                            <div className="alert alert-warning">
                                Не удалось загрузить сведения о заявках в работе: {availabilityError}
                            </div>
                        )}
                        {availability?.hasUnknownInFlight && (
                            <div className="alert alert-warning">
                                Часть заявок в работе не удалось оценить (нет курса), поэтому «в работе» —
                                нижняя граница: реально обещано больше.
                            </div>
                        )}
                        <label className="field">
                            <span>Сумма, {merchantCurrency}</span>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={operationAmount}
                                onChange={(e) => setOperationAmount(e.target.value)}
                                placeholder={operationForm.amountEditable ? "1500.50" : ""}
                                readOnly={!operationForm.amountEditable}
                                autoFocus={operationForm.amountEditable}
                            />
                            <small>
                                {operationForm.amountEditable
                                    ? "Больше нуля, не более 6 знаков после точки."
                                    : "Сумму и сторону называет выбранная строка — руками они не меняются."}
                            </small>
                        </label>
                        {/* Расхождения сверок долгового кабинета. Выбор строки — это и
                            есть ответ на вопрос «за что эти деньги»: он подставляет сумму,
                            направление и ключ, по которому сверка потом узнает проводку и
                            погасит алерт. У обычного мерчанта списка нет, и блока тоже. */}
                        {discrepancies.length > 0 && (
                            <label className="field">
                                <span>Что закрываем</span>
                                <select
                                    value={pickedDiscrepancy}
                                    onChange={(e) => {
                                        const key = e.target.value;
                                        setPickedDiscrepancy(key);
                                        const item = discrepancies.find((row) => row.operationKey === key);
                                        if (!item) return;
                                        setOperationAmount(item.amount);
                                        if (item.operationType) setOperationType(item.operationType);
                                    }}
                                >
                                    {operationForm.allowsFreeMovement ? (
                                        <option value="">— просто движение денег —</option>
                                    ) : (
                                        <option value="" disabled>
                                            — выберите расхождение —
                                        </option>
                                    )}
                                    {discrepancies.map((item) => (
                                        <option key={item.operationKey} value={item.operationKey}>
                                            {describeDiscrepancy(item)}
                                        </option>
                                    ))}
                                </select>
                                <small>
                                    Список один для пополнения и вывода: сторону называет
                                    сама строка, и форма переключится на неё. После проводки
                                    расхождение уйдёт из списка, а алерт погаснет.
                                </small>
                            </label>
                        )}
                        {/* Долговому кабинету без открытых расхождений двигать нечего:
                            свободной проводки у него нет по построению. */}
                        {operationForm.nothingToSettle && (
                            <div className="alert alert-warning">{NOTHING_TO_SETTLE_NOTICE}</div>
                        )}
                        {operationForm.listOnly && (
                            <p className="modal-hint">{LIST_ONLY_HINT}</p>
                        )}
                        {/* Вывод ограничен только балансом: заявки в работе его не
                            блокируют (леджер фиксирует реальное движение денег), но
                            админ должен видеть, что уводит баланс ниже обещанного. */}
                        {operationType === "withdrawal" && availability && withdrawalExceedsFreeFunds(
                            operationAmount, availability.knownInFlight, merchant.amount,
                        ) && (
                            <div className="alert alert-warning">
                                Сумма больше свободного остатка: часть баланса уже обещана заявкам
                                в работе. Вывод пройдёт, но после их завершения баланс может уйти в минус.
                            </div>
                        )}
                    </>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setOperationOpen(false)}
                        disabled={operationSubmitting}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-primary"
                        type="button"
                        onClick={submitOperation}
                        disabled={operationForm.submitDisabled}
                    >
                        {operationSubmitting
                            ? "Проведение…"
                            : operationType === "topup"
                                ? "Пополнить"
                                : "Вывести"}
                    </button>
                </div>
            </Modal>

            {/* Balance reset confirm */}
            <Modal
                open={resetOpen}
                title="Сбросить баланс мерчанта?"
                onClose={() => {
                    if (!resetting) setResetOpen(false);
                }}
                disableClose={resetting}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={resetError} />
                {merchant && (
                    <>
                        <p className="modal-hint">
                            Это обнулит ledger-баланс мерчанта {merchant.name} ({merchant.id}) в системе сверки.
                            Операция не отменяет заявки и не меняет историю платежей.
                        </p>
                        <div className="confirm-summary">
                            <div>
                                <span>Текущий баланс</span>
                                <strong>{formatMerchantBalance(merchant)}</strong>
                            </div>
                            <div>
                                <span>Balance ID</span>
                                <strong className="mono">{merchant.balanceId}</strong>
                            </div>
                        </div>
                    </>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setResetOpen(false)}
                        disabled={resetting}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-danger"
                        type="button"
                        onClick={confirmResetBalance}
                        disabled={resetting}
                    >
                        {resetting ? "Сброс..." : "Сбросить баланс"}
                    </button>
                </div>
            </Modal>

            <Modal
                open={archiveOpen}
                title="Архивирование мерчанта"
                onClose={() => setArchiveOpen(false)}
                sizeClassName="modal-sm"
            >
                <ErrorBanner error={archiveError} />
                <p className="modal-hint">
                    Это не hard-delete: история, баланс, ордера и платежи сохраняются.
                    Активный API-токен будет отозван, portal user отключён, все его
                    browser-сессии завершены, а мерчант пропадёт из списка.
                </p>
                {merchant && (
                    <div className="token-merchant-id mono">
                        {merchant.name} · {merchant.id}
                    </div>
                )}
                <div className="modal-actions">
                    <button
                        className="btn btn-ghost"
                        type="button"
                        onClick={() => setArchiveOpen(false)}
                        disabled={archiving}
                    >
                        Отмена
                    </button>
                    <button
                        className="btn btn-danger"
                        type="button"
                        onClick={confirmArchive}
                        disabled={archiving}
                    >
                        {archiving ? "Архивирование…" : "Архивировать"}
                    </button>
                </div>
            </Modal>
        </div>
    );
}
